import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminPageHeader } from './components/AdminMasterDetail';
import { SessionStatus } from './components/ScheduleSessionsPanel';

type SessionScope = 'all' | 'my';

type ManageSessionsProps = {
  scope?: SessionScope;
};

type RecurrenceType = 'None' | 'Weekly';
type LocationType = 'InPerson' | 'Online';
type ScheduleStatus = 'Scheduled' | 'Completed' | 'Cancelled';

interface ClassSchedule {
  id: number;
  organizationId: number;
  classroomId: number;
  classroomName: string;
  teacherUserId: number;
  teacherDisplayName: string;
  title: string;
  startDateTime: string;
  endDateTime: string;
  recurrenceType: RecurrenceType;
  recurrenceEndDate?: string | null;
  locationType: LocationType;
  location?: string | null;
  meetingUrl?: string | null;
  status: ScheduleStatus;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const mapSessionRow = (item: any): ClassSchedule => ({
  id: item.id,
  organizationId: item.organizationId ?? 0,
  classroomId: item.classroomId ?? 0,
  classroomName: item.classroomName ?? '—',
  teacherUserId: item.teacherUserId ?? 0,
  teacherDisplayName: item.teacherDisplayName ?? '',
  title: item.title ?? 'Session',
  startDateTime: item.startDateTime ?? item.startDate ?? new Date().toISOString(),
  endDateTime: item.endDateTime ?? item.endDate ?? new Date().toISOString(),
  recurrenceType: item.recurrenceType ?? 'None',
  recurrenceEndDate: item.recurrenceEndDate ?? null,
  locationType: item.locationType ?? (item.meetingUrl ? 'Online' : 'InPerson'),
  location: item.location ?? null,
  meetingUrl: item.meetingUrl ?? null,
  status: item.status ?? 'Scheduled',
  notes: item.notes ?? null,
  isActive: true,
  createdAt: item.createdAt ?? new Date().toISOString(),
  updatedAt: item.updatedAt ?? new Date().toISOString(),
});

const ManageSessions: React.FC<ManageSessionsProps> = ({ scope = 'all' }) => {
  const navigate = useNavigate();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ClassSchedule[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | SessionStatus>('all');
  const [teacherFilter, setTeacherFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isMyScope = scope === 'my';
  const pageTitle = isMyScope ? 'My Sessions' : 'All Sessions';

  const loadSessions = useCallback(async () => {
    if (!hasActiveOrganization) {
      setError('No organization selected. Please switch organization first.');
      setSessions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      let result: any[] = [];

      if (isMyScope) {
        const response = await axios.get<any[]>(`${API_BASE_URL}/schedules/my?upcomingOnly=false`, { headers });
        result = Array.isArray(response.data) ? response.data : [];
      } else {
        const response = await axios.get<any[]>(`${API_BASE_URL}/schedules`, { headers });
        const schedules = Array.isArray(response.data) ? response.data : [];
        result = schedules.flatMap((schedule) => {
          const sessionRows = Array.isArray(schedule.sessions) ? schedule.sessions : [];
          return sessionRows.map((session: any) => mapSessionRow({
            ...session,
            title: schedule.title ?? 'Session',
            location: session.location ?? schedule.location ?? null,
            meetingUrl: session.meetingUrl ?? schedule.meetingUrl ?? null,
            recurrenceType: schedule.recurrenceType ?? 'None',
            recurrenceEndDate: schedule.recurrenceEndDate ?? null,
          }));
        });
      }

      setSessions(result.map((row) => mapSessionRow(row)));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load session data.');
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, isMyScope]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const teacherOptions = useMemo(() => {
    const names = sessions
      .map((session) => session.teacherDisplayName || `User ${session.teacherUserId}`)
      .filter((name, index, array) => name && array.indexOf(name) === index)
      .sort((left, right) => left.localeCompare(right));

    return names;
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return sessions.filter((session) => {
      const teacherName = session.teacherDisplayName || `User ${session.teacherUserId}`;
      const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
      const matchesTeacher = teacherFilter === 'all' || teacherName === teacherFilter;
      const matchesSearch = !normalizedSearch || [
        session.title,
        teacherName,
        session.classroomName,
        session.location,
        session.meetingUrl,
        session.status,
      ].some((value) => (value ?? '').toLowerCase().includes(normalizedSearch));

      return matchesStatus && matchesTeacher && matchesSearch;
    });
  }, [searchTerm, sessions, statusFilter, teacherFilter]);

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12, gridTemplateRows: 'auto auto auto 1fr' }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: pageTitle, isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title={pageTitle}
        subtitle={`${sessions.length} ${isMyScope ? 'sessions for you' : 'total sessions'}`}
        actions={[
          {
            key: 'all-sessions',
            label: 'All sessions',
            variant: 'secondary',
            onClick: () => navigate('/manage-sessions/all'),
          },
          {
            key: 'my-sessions',
            label: 'My sessions',
            variant: 'secondary',
            onClick: () => navigate('/manage-sessions/mysessions'),
          },
          {
            key: 'schedule-class',
            label: 'Schedule Class',
            variant: 'primary',
            onClick: () => navigate('/manage-schedule/new'),
          },
          {
            key: 'refresh',
            label: 'Refresh',
            variant: 'secondary',
            onClick: () => { void loadSessions(); },
          },
        ]}
      />

      {!hasActiveOrganization && (
        <div style={{ borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', padding: '10px 12px', fontSize: '0.84rem' }}>
          No organization selected. Please switch organization first.
        </div>
      )}

      {error && (
        <div style={{ borderRadius: 10, border: '#fecaca', background: '#fff1f2', color: '#b91c1c', padding: '10px 12px', fontSize: '0.84rem' }}>
          {error}
        </div>
      )}

      <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 6, fontSize: 12, color: '#475569' }}>
            <span>Search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search sessions"
              style={{ border: '1px solid #dfe8f8', borderRadius: 8, padding: '8px 10px', fontSize: 14 }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6, fontSize: 12, color: '#475569' }}>
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as 'all' | SessionStatus)}
              style={{ border: '1px solid #dfe8f8', borderRadius: 8, padding: '8px 10px', fontSize: 14 }}
            >
              <option value="all">All statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="InProgress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Rescheduled">Rescheduled</option>
              <option value="NoShow">No Show</option>
            </select>
          </label>

          <label style={{ display: 'grid', gap: 6, fontSize: 12, color: '#475569' }}>
            <span>Teacher</span>
            <select
              value={teacherFilter}
              onChange={(event) => setTeacherFilter(event.target.value)}
              style={{ border: '1px solid #dfe8f8', borderRadius: 8, padding: '8px 10px', fontSize: 14 }}
            >
              <option value="all">All teachers</option>
              {teacherOptions.map((teacher) => (
                <option key={teacher} value={teacher}>{teacher}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', overflow: 'hidden', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #edf2fb', fontWeight: 700, color: '#173159', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span>Session List</span>
          <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>{filteredSessions.length} results</span>
        </div>

        {loading ? (
          <div style={{ padding: 14, color: '#475569' }}>Loading sessions...</div>
        ) : filteredSessions.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, padding: 14, color: '#64748b' }}>No sessions found.</div>
        ) : (
          <div style={{ overflowX: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fbff', color: '#334155' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Teacher</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Classroom</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Location</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session) => {
                  const teacherName = session.teacherDisplayName || `User ${session.teacherUserId}`;
                  const start = new Date(session.startDateTime);
                  const end = new Date(session.endDateTime);

                  return (
                    <tr key={session.id} style={{ borderBottom: '1px solid #edf2fb' }}>
                      <td style={{ padding: '8px 10px' }}>{start.toLocaleDateString()}</td>
                      <td style={{ padding: '8px 10px' }}>{`${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</td>
                      <td style={{ padding: '8px 10px' }}>{session.title}</td>
                      <td style={{ padding: '8px 10px' }}>{teacherName}</td>
                      <td style={{ padding: '8px 10px' }}>{session.classroomName || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{session.location || session.meetingUrl || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{session.status}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        <button type="button" className="amd-btn amd-btn-secondary" onClick={() => navigate(`/manage-sessions/${session.id}`)} style={{ marginRight: 6 }}>
                          Open
                        </button>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => navigate(`/manage-schedule/${session.id}`)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSessions;
